import { useMutation } from '@apollo/client';
import {
  Button,
  EETextArea,
  ErrorMessage,
  Modal,
  SEND_COMPANY_INVITE,
  GET_COMPANY_INVITATIONS,
} from '@employ-engage/common-components';
import { useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export interface InviteModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onInviteSent?: () => void;
}

export const InviteModal = ({
  open,
  setOpen,
  onInviteSent,
}: InviteModalProps) => {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(false);
  const [failures, setFailures] = useState<string[]>([]);
  const emailAddressesRef = useRef<HTMLTextAreaElement>(null);

  const [sendInvite] = useMutation(SEND_COMPANY_INVITE, {
    refetchQueries: [{ query: GET_COMPANY_INVITATIONS }],
  });

  const { handleSubmit, handleChange, errors, values, touched, resetForm } =
    useFormik({
      initialValues: {
        emailAddresses: '',
      },
      onSubmit: async (values) => {
        setLoading(true);
        setFailures([]);

        const emailAddresses = values.emailAddresses
          .split(',')
          .map((e) => e.trim().replace(/,$/, ''))
          .filter(Boolean);

        // One address failing — already a member, already invited — must not
        // discard the others, and the modal must not close as though every
        // invitation went out.
        const results = await Promise.allSettled(
          emailAddresses.map((email) => sendInvite({ variables: { email } }))
        );

        const rejected = results
          .map((result, index) =>
            result.status === 'rejected'
              ? `${emailAddresses[index]}: ${
                  (result.reason as Error)?.message ?? t('could not be invited')
                }`
              : null
          )
          .filter(Boolean) as string[];

        setLoading(false);
        setFailures(rejected);
        onInviteSent?.();

        if (!rejected.length) {
          resetForm();
          setOpen(false);
        }
      },
      validationSchema: Yup.object().shape({
        emailAddresses: Yup.string()
          .required(t('Email addresses are required'))
          .test(
            'is-valid-emails',
            t('One or more email addresses are invalid'),
            (value) => {
              if (!value) return false;
              const emails = value
                .split(',')
                .map((email) => email.trim().replace(/,$/, ''));
              const invalidEmails = emails.filter(
                (email) => !EMAIL_REGEX.test(email) && email !== ''
              );
              if (invalidEmails.length > 0) {
                return new Yup.ValidationError(
                  t('Invalid email addresses:') +
                    ` ${invalidEmails.join(', ')}`,
                  null,
                  'emailAddresses'
                );
              }
              return true;
            }
          ),
      }),
    });

  const shouldDisableInviteButton =
    loading || !values.emailAddresses || errors.emailAddresses;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      panelClassName="w-[800px] max-w-full"
      initialFocus={emailAddressesRef}
    >
      <form className="p-6" onSubmit={handleSubmit}>
        <div className="text-lg font-bold">{t('Invite Team Member')}</div>
        <div className="mt-4">
          <EETextArea
            ref={emailAddressesRef}
            name="emailAddresses"
            value={values.emailAddresses}
            onChange={handleChange}
            placeholder={t('Email Addresses separated by commas')}
          />
          <ErrorMessage
            errors={errors.emailAddresses}
            touched={touched.emailAddresses}
          />
        </div>
        <div className="mt-4">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={!!shouldDisableInviteButton}
            className="disabled:opacity-50"
          >
            {t('Invite')}
          </Button>
        </div>
        {!!failures.length && (
          <ul
            className="mt-4 space-y-1 rounded bg-semantic-error/20 p-3 text-sm"
            role="alert"
          >
            {failures.map((failure) => (
              <li key={failure}>{failure}</li>
            ))}
          </ul>
        )}
      </form>
    </Modal>
  );
};

export default InviteModal;
